import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import { HttpClient, HttpParams } from "@angular/common/http";
import { LoggerService } from "./logging/logger.service";
import { catchError } from "rxjs/operators";
import { Observable } from "rxjs";
import { ErrorHandlingService } from "./error-handling.service";
import {
  NewSalesOrderBatch,
  SalesOrderBatch,
} from "../insurgo-interfaces/sales-order-batch";
import {
  NewSalesOrder,
  SalesOrder,
  SalesReport,
  TapesOnSales,
} from "../insurgo-interfaces/sales-order";

@Injectable({
  providedIn: "root",
})
export class SalesOrderService {
  apiUrl = environment.apiUrl;

  constructor(
    public authHttp: HttpClient,
    private readonly logger: LoggerService,
    private errorHandlingService: ErrorHandlingService
  ) {}

  getAllSalesOrders(): Observable<SalesOrder[]> {
    let URL = `${this.apiUrl}/sales-order`;

    return this.authHttp.get<SalesOrder[]>(URL).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  getSalesOrderByID(salesOrderUUID: string): Observable<SalesOrder> {
    let URL = `${this.apiUrl}/sales-order/${salesOrderUUID}`;

    return this.authHttp.get<SalesOrder>(URL).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  getSalesOrdersByStatus(
    status: number,
    getQuantities: boolean = false,
    startDateSec?: number,
    endDateSec?: number
  ): Observable<SalesOrder[]> {
    let URL = `${this.apiUrl}/sales-order/by-status`;
    let params = new HttpParams().set("status", status.toString());
    params = params.set("getQuantities", getQuantities.toString());
    if (startDateSec) {
      params = params.set("startDateSec", startDateSec.toString());
    }
    if (endDateSec) {
      params = params.set("endDateSec", endDateSec.toString());
    }

    return this.authHttp.get<SalesOrder[]>(URL, { params }).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  updateSalesOrder(
    salesOrderUUID: string,
    salesOrder: NewSalesOrder
  ): Observable<number> {
    let URL = `${this.apiUrl}/sales-order/${salesOrderUUID}`;

    const data = {
      insurgoOrderNumber: salesOrder.insurgoOrderNumber,
      customerOrderNumber: salesOrder.customerOrderNumber,
      customerID: salesOrder.customerID,
      deliveryCost: salesOrder.deliveryCost,
      deliveryCurrency: salesOrder.deliveryCurrency,
      status: salesOrder.status,
      orderDateSec: salesOrder.orderDateSec,
    };

    console.log("Updating Sales Order:", data);

    return this.authHttp.put<number>(URL, data).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  updateSalesOrderBatch(
    salesOrderBatchUUID: string,
    salesOrderBatch: NewSalesOrderBatch
  ): Observable<number> {
    let URL = `${this.apiUrl}/sales-order-batch/${salesOrderBatchUUID}`;

    const data = {
      manufacturer: salesOrderBatch.manufacturer,
      quantity: salesOrderBatch.quantity,
      pricePerTape: salesOrderBatch.pricePerTape,
      statusCondition: salesOrderBatch.statusCondition,
      type: salesOrderBatch.type,
    };

    return this.authHttp.put<number>(URL, data).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  createSalesOrder(salesOrder: NewSalesOrder): Observable<number> {
    let URL = `${this.apiUrl}/sales-order`;

    return this.authHttp.post<number>(URL, salesOrder).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  createSalesOrderBatch(
    salesOrderUUID: string,
    salesOrderBatch: NewSalesOrderBatch
  ): Observable<number> {
    let URL = `${this.apiUrl}/sales-order-batch`;

    const data = {
      salesOrderUUID,
      type: salesOrderBatch.type,
      manufacturer: salesOrderBatch.manufacturer,
      quantity: salesOrderBatch.quantity,
      pricePerTape: salesOrderBatch.pricePerTape,
      statusCondition: salesOrderBatch.statusCondition,
    };

    return this.authHttp.post<number>(URL, data).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  getSalesOrderBatches(salesOrderUUID: string): Observable<SalesOrderBatch[]> {
    let URL = `${this.apiUrl}/sales-order-batch/${salesOrderUUID}`;

    return this.authHttp.get<SalesOrderBatch[]>(URL).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  getSalesOrderBatchesWithTapeCount(
    salesOrderUUID: string
  ): Observable<SalesOrderBatch[]> {
    let URL = `${this.apiUrl}/sales-order-batch/${salesOrderUUID}/with-tape-count`;

    return this.authHttp.get<SalesOrderBatch[]>(URL).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  getSalesOrderBatch(salesOrderBatchUUID: string): Observable<SalesOrderBatch> {
    let URL = `${this.apiUrl}/sales-order-batch/batch/${salesOrderBatchUUID}`;

    return this.authHttp.get<SalesOrderBatch>(URL).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  getTapesOnSalesOrderBatch(
    salesOrderBatchUUID: string
  ): Observable<TapesOnSales[]> {
    let URL = `${this.apiUrl}/sales-order-batch/tapes/${salesOrderBatchUUID}`;

    return this.authHttp.get<TapesOnSales[]>(URL).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  getTapesOnSalesOrder(salesOrderUUID: string): Observable<TapesOnSales[]> {
    let URL = `${this.apiUrl}/sales-order/tapes/${salesOrderUUID}`;
    return this.authHttp.get<TapesOnSales[]>(URL).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  getSalesReport(
    startDateSec: number,
    endDateSec: number
  ): Observable<SalesReport> {
    let URL = `${this.apiUrl}/sales-order/report`;
    let params = new HttpParams();
    if (startDateSec) {
      params = params.set("startDateSec", startDateSec.toString());
    }
    if (endDateSec) {
      params = params.set("endDateSec", endDateSec.toString());
    }

    return this.authHttp.get<SalesReport>(URL, { params }).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  deleteSalesOrder(salesOrderID: number): Observable<void> {
    let URL = `${this.apiUrl}/sales-order/${salesOrderID}`;

    return this.authHttp.delete<void>(URL).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }

  deleteSalesOrderBatch(salesOrderBatchID: string): Observable<void> {
    let URL = `${this.apiUrl}/sales-order-batch/${salesOrderBatchID}`;

    return this.authHttp.delete<void>(URL).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }
  removeTapeFromSalesOrderBatch(
    tapeID: number,
    batchUUID: number
  ): Observable<void> {
    let URL = `${this.apiUrl}/sales-order-batch/tape`;

    const data = {
      tapeID,
      salesOrderBatchUUID: batchUUID,
    };

    return this.authHttp.patch<void>(URL, data).pipe(
      catchError((error) => {
        return this.errorHandlingService.handleErrorTrustedRoute(error);
      })
    );
  }
}
